import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _typeof from '@babel/runtime/helpers/typeof';

/* eslint-disable no-empty */
/*
 * loglevel - https://github.com/pimterry/loglevel
 *
 * Copyright (c) 2013 Tim Perry
 * Licensed under the MIT license.
 */
function logLevel() {
  // Slightly dubious tricks to cut down minimized file size
  var noop = function noop() {};
  var undefinedType = 'undefined';
  var isIE = (typeof window === "undefined" ? "undefined" : _typeof(window)) !== undefinedType && _typeof(window.navigator) !== undefinedType && /Trident\/|MSIE /.test(window.navigator.userAgent);
  var logMethods = ['trace', 'debug', 'info', 'warn', 'error'];

  // Cross-browser bind equivalent that works at least back to IE6
  function bindMethod(obj, methodName) {
    var method = obj[methodName];
    if (typeof method.bind === 'function') {
      return method.bind(obj);
    } else {
      try {
        return Function.prototype.bind.call(method, obj);
      } catch (e) {
        // Missing bind shim or IE8 + Modernizr, fallback to wrapping
        return function () {
          return Function.prototype.apply.apply(method, [obj, arguments]);
        };
      }
    }
  }

  // Trace() doesn't print the message in IE, so for that case we need to wrap it
  function traceForIE() {
    if (console.log) {
      if (console.log.apply) {
        console.log.apply(console, arguments);
      } else {
        // In old IE, native console methods themselves don't have apply().
        Function.prototype.apply.apply(console.log, [console, arguments]);
      }
    }
    if (console.trace) console.trace();
  }

  // Build the best logging method possible for this env
  // Wherever possible we want to bind, not wrap, to preserve stack traces
  function realMethod(methodName) {
    if (methodName === 'debug') {
      methodName = 'log';
    }
    if ((typeof console === "undefined" ? "undefined" : _typeof(console)) === undefinedType) {
      return false; // No method possible, for now - fixed later by enableLoggingWhenConsoleArrives
    } else if (methodName === 'trace' && isIE) {
      return traceForIE;
    } else if (console[methodName] !== undefined) {
      return bindMethod(console, methodName);
    } else if (console.log !== undefined) {
      return bindMethod(console, 'log');
    } else {
      return noop;
    }
  }

  // These private functions always need `this` to be set properly

  function replaceLoggingMethods(level, loggerName) {
    /*jshint validthis:true */
    for (var i = 0; i < logMethods.length; i++) {
      var methodName = logMethods[i];
      this[methodName] = i < level ? noop : this.methodFactory(methodName, level, loggerName);
    }

    // Define log.log as an alias for log.debug
    this.log = this.debug;
  }

  // In old IE versions, the console isn't present until you first open it.
  // We build realMethod() replacements here that regenerate logging methods
  function enableLoggingWhenConsoleArrives(methodName, level, loggerName) {
    return function () {
      if ((typeof console === "undefined" ? "undefined" : _typeof(console)) !== undefinedType) {
        replaceLoggingMethods.call(this, level, loggerName);
        this[methodName].apply(this, arguments);
      }
    };
  }

  // By default, we use closely bound real methods wherever possible, and
  // otherwise we wait for a console to appear, and then try again.
  // eslint-disable-next-line no-unused-vars
  function defaultMethodFactory(methodName, _level, _loggerName) {
    /*jshint validthis:true */
    return realMethod(methodName) || enableLoggingWhenConsoleArrives.apply(this, arguments);
  }
  function Logger(name, defaultLevel, factory) {
    var self = this;
    var currentLevel;
    defaultLevel = defaultLevel == null ? 'WARN' : defaultLevel;
    var storageKey = 'loglevel';
    if (typeof name === 'string') {
      storageKey += ':' + name;
    } else if (_typeof(name) === 'symbol') {
      storageKey = undefined;
    }
    function persistLevelIfPossible(levelNum) {
      var levelName = (logMethods[levelNum] || 'silent').toUpperCase();
      if ((typeof window === "undefined" ? "undefined" : _typeof(window)) === undefinedType || !storageKey) return;

      // Use localStorage if available
      try {
        window.localStorage[storageKey] = levelName;
        return;
      } catch (ignore) {}

      // Use session cookie as fallback
      try {
        window.document.cookie = encodeURIComponent(storageKey) + '=' + levelName + ';';
      } catch (ignore) {}
    }
    function getPersistedLevel() {
      var storedLevel;
      if ((typeof window === "undefined" ? "undefined" : _typeof(window)) === undefinedType || !storageKey) return;
      try {
        storedLevel = window.localStorage[storageKey];
      } catch (ignore) {}

      // Fallback to cookies if local storage gives us nothing
      if (_typeof(storedLevel) === undefinedType) {
        try {
          var cookie = window.document.cookie;
          var location = cookie.indexOf(encodeURIComponent(storageKey) + '=');
          if (location !== -1) {
            storedLevel = /^([^;]+)/.exec(cookie.slice(location))[1];
          }
        } catch (ignore) {}
      }

      // If the stored level is not valid, treat it as if nothing was stored.
      if (self.levels[storedLevel] === undefined) {
        storedLevel = undefined;
      }
      return storedLevel;
    }
    function clearPersistedLevel() {
      if ((typeof window === "undefined" ? "undefined" : _typeof(window)) === undefinedType || !storageKey) return;

      // Use localStorage if available
      try {
        window.localStorage.removeItem(storageKey);
        return;
      } catch (ignore) {}

      // Use session cookie as fallback
      try {
        window.document.cookie = encodeURIComponent(storageKey) + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC';
      } catch (ignore) {}
    }

    /*
     *
     * Public logger API - see https://github.com/pimterry/loglevel for details
     *
     */

    self.name = name;
    self.levels = {
      TRACE: 0,
      DEBUG: 1,
      INFO: 2,
      WARN: 3,
      ERROR: 4,
      SILENT: 5
    };
    self.methodFactory = factory || defaultMethodFactory;
    self.getLevel = function () {
      return currentLevel;
    };
    self.setLevel = function (level, persist) {
      if (typeof level === 'string' && self.levels[level.toUpperCase()] !== undefined) {
        level = self.levels[level.toUpperCase()];
      }
      if (typeof level === 'number' && level >= 0 && level <= self.levels.SILENT) {
        currentLevel = level;
        if (persist !== false) {
          // defaults to true
          persistLevelIfPossible(level);
        }
        replaceLoggingMethods.call(self, level, name);
        if ((typeof console === "undefined" ? "undefined" : _typeof(console)) === undefinedType && level < self.levels.SILENT) {
          return 'No console available for logging';
        }
      } else {
        throw 'log.setLevel() called with invalid level: ' + level;
      }
    };
    self.setDefaultLevel = function (level) {
      defaultLevel = level;
      if (!getPersistedLevel()) {
        self.setLevel(level, false);
      }
    };
    self.resetLevel = function () {
      self.setLevel(defaultLevel, false);
      clearPersistedLevel();
    };
    self.enableAll = function (persist) {
      self.setLevel(self.levels.TRACE, persist);
    };
    self.disableAll = function (persist) {
      self.setLevel(self.levels.SILENT, persist);
    };

    // Initialize with the right level
    var initialLevel = getPersistedLevel();
    if (initialLevel == null) {
      initialLevel = defaultLevel;
    }
    self.setLevel(initialLevel, false);
  }

  /*
   *
   * Top-level API
   *
   */

  var defaultLogger = new Logger();
  var _loggersByName = {};
  defaultLogger.getLogger = function getLogger(name) {
    if (_typeof(name) !== 'symbol' && typeof name !== 'string' || name === '') {
      throw new TypeError('You must supply a name when creating a logger.');
    }
    var logger = _loggersByName[name];
    if (!logger) {
      logger = _loggersByName[name] = new Logger(name, defaultLogger.getLevel(), defaultLogger.methodFactory);
    }
    return logger;
  };

  // Grab the current global log variable in case of overwrite
  var _logger = (typeof window === "undefined" ? "undefined" : _typeof(window)) !== undefinedType ? window.logger : undefined;
  defaultLogger.noConflict = function () {
    if ((typeof window === "undefined" ? "undefined" : _typeof(window)) !== undefinedType && window.logger === defaultLogger) {
      window.logger = _logger;
    }
    return defaultLogger;
  };
  defaultLogger.getLoggers = function getLoggers() {
    return _loggersByName;
  };

  // ES6 default export, for compatibility
  defaultLogger['default'] = defaultLogger;
  return defaultLogger;
}

var win = window;
if (!win) {
  throw new Error('Plugin for browser usage only');
}
function uiLogsRemoteStore(config) {
  var storeKey = config.logsStoreKey;
  var allUILogs = JSON.parse(localStorage.getItem(storeKey) || '[]');
  var initiateBatchIntervalId = null;
  var getLogsFromStore = function getLogsFromStore(count) {
    return allUILogs.slice(0, count);
  };
  var clearLogsFromStore = function clearLogsFromStore(count) {
    allUILogs = allUILogs.slice(count);
    localStorage.setItem(storeKey, JSON.stringify(allUILogs));
  };
  var sendLogsToServer = function sendLogsToServer(sentRecords) {
    var _ref = config || {},
      method = _ref.method,
      url = _ref.url,
      headers = _ref.headers;
    var xhr = new window.XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    for (var header in headers) {
      if (hasOwnProperty.call(headers, header)) {
        var value = headers[header];
        if (value) {
          xhr.setRequestHeader(header, value);
        }
      }
    }
    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4) {
        return;
      }
      if (xhr.status === 200) {
        clearLogsFromStore(sentRecords.length);
      }
    };
    xhr.send(JSON.stringify({
      logs: sentRecords
    }));
  };
  return {
    stopLogsBatchProcess: function stopLogsBatchProcess() {
      clearInterval(initiateBatchIntervalId);
      initiateBatchIntervalId = null;
    },
    addLogToStore: function addLogToStore(log) {
      allUILogs.push(log);
      localStorage.setItem(storeKey, JSON.stringify(allUILogs));
    },
    initiateLogsBatchProcess: function initiateLogsBatchProcess() {
      var _ref2 = config || {},
        _ref2$batchInterval = _ref2.batchInterval,
        batchInterval = _ref2$batchInterval === void 0 ? 20000 : _ref2$batchInterval,
        _ref2$logsPerBatch = _ref2.logsPerBatch,
        logsPerBatch = _ref2$logsPerBatch === void 0 ? 20 : _ref2$logsPerBatch;
      initiateBatchIntervalId = setInterval(function () {
        var sentRecords = getLogsFromStore(logsPerBatch);
        if (sentRecords.length) {
          sendLogsToServer(sentRecords);
        }
      }, batchInterval);
    }
  };
}
function getStacktrace() {
  try {
    throw new Error();
  } catch (trace) {
    return trace.stack;
  }
}
var uiLogger = null;
var hasStacktraceSupport = !!getStacktrace();
var storeLogsPlugin = function storeLogsPlugin(logger, config, storeCallbackFunc) {
  if (!logger || !logger.getLogger) {
    throw new TypeError('Argument is not a root uiLogger object');
  }
  if (uiLogger) {
    throw new Error('You can assign a plugin only one time');
  }
  uiLogger = logger;
  var originalFactory = logger.methodFactory;
  var pluginFactory = function pluginFactory(methodName, logLevel, loggerName) {
    var rawMethod = originalFactory(methodName, logLevel, loggerName);
    var needStack = hasStacktraceSupport && config.stacktrace.levels.some(function (level) {
      return level === methodName;
    });
    var levelVal = uiLogger.levels[methodName.toUpperCase()];
    var needLog = levelVal >= uiLogger.levels[config.level.toUpperCase()];
    return function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      if (needLog) {
        var timestamp = config.timestamp();
        var stacktrace = needStack ? getStacktrace() : '';
        if (stacktrace) {
          var lines = stacktrace.split('\n');
          lines.splice(0, config.stacktrace.excess + 3);
          var depth = config.stacktrace.depth;
          if (depth && lines.length !== depth + 1) {
            var shrink = lines.splice(0, depth);
            stacktrace = shrink.join('  ');
            if (lines.length) stacktrace += " and ".concat(lines.length, " more");
          } else {
            stacktrace = lines.join('  ');
          }
        }
        var log = {
          timestamp: timestamp,
          level: methodName,
          message: "".concat(loggerName ? "".concat(loggerName, " :") : '').concat(args.join(' ')).concat(stacktrace ? "".concat(stacktrace) : ''),
          url: win.location.href || ''
          // level: { label: methodName, value: levelVal },
        };

        storeCallbackFunc(log);
      }
      rawMethod.apply(void 0, args);
    };
  };
  logger.methodFactory = pluginFactory;
  logger.setLevel(logger.getLevel());
  return logger;
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var defaultConfig = {
  url: '/apps/ptb/api/logger',
  method: 'POST',
  headers: {},
  logsStoreKey: 'PTB_UI_LOGS',
  logsPerBatch: 20,
  batchInterval: 20000,
  level: 'trace',
  timestamp: function timestamp() {
    return new Date().toISOString();
  },
  stacktrace: {
    levels: ['trace', 'warn', 'error'],
    depth: 3,
    excess: 0
  }
};
var logger = function logger() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var config = _objectSpread(_objectSpread(_objectSpread({}, defaultConfig), options || {}), {}, {
    stacktrace: _objectSpread(_objectSpread({}, defaultConfig.stacktrace), (options === null || options === void 0 ? void 0 : options.stacktrace) || {})
  });
  var logger = logLevel();
  var remoteStore = uiLogsRemoteStore(config);
  remoteStore.initiateLogsBatchProcess();
  logger.remoteStore = remoteStore;
  storeLogsPlugin(logger, config, remoteStore.addLogToStore);
  window.logger = logger;
  return logger;
};

export { logger };
//# sourceMappingURL=index.esm.js.map
